import {Component, OnDestroy, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import {
  FetchNotificationListRequest,
  SendMarkAsCheckedNotificationData,
} from "../core/notifications.actions";
import {NotificationItemModel} from "../model/notification-model";
import {Subject, takeUntil} from "rxjs";
import {fetchNotificationList} from "../core/notifications.selectors";
import {ActivatedRoute, Router} from "@angular/router";
import {CommonService} from "../../../shared/services/common.service";
import {Sort} from '@angular/material/sort';
import {sortOrderMap} from 'src/app/modules/shared/constants/common.constants';
import {
  DeleteNotificationRequest,
  MarkNotificationAsReadRequest
} from "../../../shared/core/shared.actions";
import {FormControl, FormGroup} from "@angular/forms";
import {DropdownOption} from "../../../../models/dropdown-option.model";

@Component({
  selector: 'app-notifications-screen',
  templateUrl: './notifications-screen.component.html',
  styleUrls: ['./notifications-screen.component.css']
})
export class NotificationsScreenComponent implements OnInit, OnDestroy {
  expandPanel: any = true;
  notificationList: NotificationItemModel[] = [];
  loading: boolean = false
  notificationDisplayColumn: any[] = [
    'selected',
    'title',
    'createdAt',
    'message'
  ];
  sortColumn: any;
  sortOrder: any;
  currentPageNumber: number = 0;
  hasNext: boolean = false;
  queryParams;
  sortOrderMap = sortOrderMap;
  form: FormGroup = new FormGroup({
    filters: new FormControl('', [])
  });
  allSelection: boolean = false;
  allIndeterminate: boolean = false;
  filterKeyMap = {
    sortByUnread: 1,
    sortByRead: 2,
    markRead: 3
  }

  filterOptions: DropdownOption[] = [
    {
      value: 'Sort by unread',
      key: this.filterKeyMap.sortByUnread
    },
    {
      value: 'Sort by read',
      key: this.filterKeyMap.sortByRead
    },
    {
      value: 'Mark as read',
      key: this.filterKeyMap.markRead
    }
  ];

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private commonService: CommonService,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.store.dispatch(FetchNotificationListRequest({payload: {pageNumber: 1}}))
    this.store.select(fetchNotificationList).pipe(takeUntil(this.onDestroy))
      .subscribe(notification => {
          this.currentPageNumber = notification.notification.pageNumber;
          this.hasNext = notification.notification.hasNext
          this.loading = notification.loading
          this.notificationList = [...notification.notification.list];
          this.setAllSelectionDefaultState();
          if (this.hasNext) {
            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });

              if(this.sortColumn && !this.loading && this.currentPageNumber <= 2){
                this.commonService.scrollColumnInView(this.sortColumn)
              }

            }, 0)
          }
        }
      );


    this.form.controls['filters'].valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(values => {
        if (values === this.filterKeyMap.sortByRead || values === this.filterKeyMap.sortByUnread) {
          this.sortDataByRead(values)
        } else if (values === this.filterKeyMap.markRead) {
          this.markMultipleNotificationAsRead();
        } else if (!values && this.sortColumn === 'read') {
          this.store.dispatch(FetchNotificationListRequest({payload: {pageNumber: 1}}))
        }
      })

    this.commonService
      .onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams
        }
        const prevUrlParams = this.commonService.urlParams.getValue();
        
        if (d.isRouteChanged || JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)) {
         this.commonService.urlParams.next(urlParams);
        }
      });

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy))
      .subscribe((queryParams: any) => {
        this.queryParams = queryParams.params
      })
  }

  onScroll() {
    if (this.hasNext && !this.loading) {
      const newPage = this.currentPageNumber + 1;
      let sortOrder = this.getSortOrder();
      this.store.dispatch(FetchNotificationListRequest(
        {
          payload: {
            pageNumber: newPage,
            ...(this.sortColumn && sortOrder ? {sortColumn: this.sortColumn} : {}),
            ...(this.sortColumn && sortOrder ? {sortOrder: sortOrder} : {})
          }
        }
      ))
    }
  }

  fetchSortedData() {
    let sortOrder = this.getSortOrder();
    this.store.dispatch(FetchNotificationListRequest(
      {
        payload: {
          pageNumber: 1,
          ...(this.sortColumn && sortOrder ? {sortColumn: this.sortColumn} : {}),
          ...(this.sortColumn && sortOrder ? {sortOrder: sortOrder} : {})
        }
      }
    ))
  }

  sortData(sort: Sort) {
    this.sortColumn = sort.active;
    this.sortOrder = sort.direction;
    this.fetchSortedData();
  }

  sortDataByRead(value: number) {
    if (value === this.filterKeyMap.sortByRead) {
      this.sortColumn = 'read';
      this.sortOrder = 'desc'
    } else if (value === this.filterKeyMap.sortByUnread) {
      this.sortColumn = 'read';
      this.sortOrder = 'asc'
    }
    this.fetchSortedData();
  }

  getSortOrder(): number {
    return this.sortOrderMap[this.sortOrder]
  }

  getNotificationDetails(notificationId: NotificationItemModel) {
    const activeRoute = this.router.url.split('?');
    const notificationQueryParams = {
      ...this.queryParams,
      notificationId: notificationId.id,
    }
    this.router.navigate([`/${activeRoute[0]}`], {queryParams: notificationQueryParams});
    if (!notificationId.read) {
      this.markNotificationAsRead(notificationId.id)
    }
  }

  markNotificationAsRead(notificationId: string) {
    this.store.dispatch(MarkNotificationAsReadRequest({payload: {notificationId: [notificationId]}}))
  }

  onClickDropDown(event) {
    event.stopPropagation()
  }

  onCheckboxClicked(event, notification, index: number) {
    event.stopPropagation();
    const updatedData = this.notificationList.map(item => {
      if (item.id === notification.id) {
        return {...item, selected: !item.selected}
      } else {
        return {
          ...item
        }
      }
    });
    const isAnySelected = updatedData.filter(item => item.selected);
    if (isAnySelected.length > 0 && isAnySelected.length !== this.notificationList.length) {
      this.allSelection = true;
      this.allIndeterminate = true;
    } else if (isAnySelected.length > 0 && isAnySelected.length === this.notificationList.length) {
      this.allIndeterminate = false;
      this.allSelection = true;
    } else if (isAnySelected.length === 0) {
      this.allIndeterminate = false;
    }
    this.store.dispatch(SendMarkAsCheckedNotificationData({
      payload: {
        list: updatedData
      }
    }))
  }

  deleteSelectedNotification(event) {
    event.stopPropagation();
    const selectedNotificationIds: string[] = this.getSelectedNotificationsId()
    if (selectedNotificationIds.length) {
      this.store.dispatch(DeleteNotificationRequest({payload: {notificationId: [...selectedNotificationIds]}}))
    }
  }

  getSelectedNotificationsId() {
    return this.notificationList.filter(item => item.selected === true)
      .map(selectedNotification => selectedNotification.id);
  }

  markMultipleNotificationAsRead() {
    const selectedNotificationIds: string[] = this.getSelectedNotificationsId()
    if (selectedNotificationIds.length) {
      this.form.patchValue({
        filters: ''
      }, {emitEvent: false});
      this.store.dispatch(MarkNotificationAsReadRequest({payload: {notificationId: [...selectedNotificationIds]}}))
    }
  }

  selectedAllCheckboxClicked(event) {
    let updatedData: NotificationItemModel[] = []
    if (event.checked && !this.allIndeterminate) {
      updatedData = this.notificationList.map(item => ({...item, selected: true}))
    } else if (event.checked && this.allIndeterminate) {
      updatedData = this.notificationList.map(item => ({...item, selected: true}))
    } else if (!event.checked && this.allIndeterminate) {
      updatedData = this.notificationList.map(item => ({...item, selected: false}))
    } else if (!event.checked && !this.allIndeterminate) {
      updatedData = this.notificationList.map(item => ({...item, selected: false}))
    }
    this.store.dispatch(SendMarkAsCheckedNotificationData({
      payload: {
        list: updatedData
      }
    }))
  }

  setAllSelectionDefaultState() {
    const isAnySelected = this.notificationList.filter(item => item.selected);
    if (isAnySelected.length > 0 && isAnySelected.length !== this.notificationList.length) {
      this.allSelection = true;
      this.allIndeterminate = true;
    } else if (isAnySelected.length > 0 && isAnySelected.length === this.notificationList.length) {
      this.allIndeterminate = false;
      this.allSelection = true;
    } else if (isAnySelected.length === 0) {
      this.allIndeterminate = false;
      this.allSelection = false;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
